import translations from './translations.json';

/**
 * Internationalization (i18n) utility
 * Provides a scalable way to manage multi-lingual content
 */
class I18n {
  constructor() {
    this.currentLanguage = 'en-US';
    this.translations = translations;
    this.fallbackLanguage = 'en-US';
  }

  /**
   * Set the current language
   * @param {string} langCode - Language code (e.g., 'en-US', 'ar')
   */
  setLanguage(langCode) {
    if (this.translations[langCode]) {
      this.currentLanguage = langCode;
    } else {
      // console.warn(`Language ${langCode} not found, falling back to ${this.fallbackLanguage}`);
      this.currentLanguage = this.fallbackLanguage;
    }
  }

  /**
   * Get the current language code
   * @returns {string} Current language code
   */
  getLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get a translation by key path
   * @param {string} keyPath - Dot-separated key path (e.g., 'messages.accessDenied')
   * @param {object} params - Optional parameters for string interpolation
   * @returns {string} Translated string or the key path if not found
   */
  t(keyPath, params = {}) {
    const keys = keyPath.split('.');
    let translation = this.translations[this.currentLanguage];

    // Navigate through the nested object
    for (const key of keys) {
      if (translation && translation[key]) {
        translation = translation[key];
      } else {
        // Fallback to default language
        translation = this.translations[this.fallbackLanguage];
        for (const fallbackKey of keys) {
          if (translation && translation[fallbackKey]) {
            translation = translation[fallbackKey];
          } else {
            // console.warn(`Translation key "${keyPath}" not found`);
            return keyPath; // Return the key path if translation not found
          }
        }
        break;
      }
    }

    // If translation is a string, apply parameter interpolation
    if (typeof translation === 'string') {
      return this.interpolate(translation, params);
    }

    return translation || keyPath;
  }

  /**
   * Interpolate parameters into a translation string
   * @param {string} str - String with placeholders like {{param}}
   * @param {object} params - Parameters to interpolate
   * @returns {string} Interpolated string
   */
  interpolate(str, params) {
    return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }

  /**
   * Get all translations for a specific section
   * @param {string} section - Section name (e.g., 'messages', 'labels')
   * @returns {object} Object containing all translations for that section
   */
  getSection(section) {
    const sectionData = this.translations[this.currentLanguage]?.[section];
    if (!sectionData) {
      return this.translations[this.fallbackLanguage]?.[section] || {};
    }
    return sectionData;
  }

  /**
   * Check if a language is available
   * @param {string} langCode - Language code to check
   * @returns {boolean} True if language is available
   */
  isLanguageAvailable(langCode) {
    return !!this.translations[langCode];
  }

  /**
   * Get list of available languages
   * @returns {string[]} Array of available language codes
   */
  getAvailableLanguages() {
    return Object.keys(this.translations);
  }

  /**
   * Initialize language based on Office context or browser settings
   * Prioritizes Office.context.displayLanguage for Outlook language detection
   */
  initializeLanguage() {
    // Priority 1: Try to get language from Office context (Outlook language)
    if (typeof Office !== 'undefined' && Office.context && Office.context.displayLanguage) {
      const officeLang = Office.context.displayLanguage;
      // console.log('Office display language detected:', officeLang);
      
      // Try exact match first
      if (this.isLanguageAvailable(officeLang)) {
        this.setLanguage(officeLang);
        // console.log('Language set to:', officeLang);
        return;
      }
      
      // Try to match by base language (e.g., 'ar-SA' -> 'ar', 'en-GB' -> 'en-US')
      const langBase = officeLang.split('-')[0];
      const availableLangs = this.getAvailableLanguages();
      
      // First try to find a language that starts with the base (e.g., 'ar' matches 'ar')
      const matchingLang = availableLangs.find(lang => {
        const langBaseCode = lang.split('-')[0];
        return langBaseCode === langBase;
      });
      
      if (matchingLang) {
        this.setLanguage(matchingLang);
        // console.log(`Language matched: ${officeLang} -> ${matchingLang}`);
        return;
      }
    }

    // Priority 2: Fallback to browser language
    const browserLang = navigator.language || navigator.userLanguage;
    // console.log('Browser language detected:', browserLang);
    
    if (this.isLanguageAvailable(browserLang)) {
      this.setLanguage(browserLang);
      // console.log('Language set to:', browserLang);
      return;
    }

    // Try language without region (e.g., 'en' from 'en-GB')
    const langBase = browserLang.split('-')[0];
    const availableLangs = this.getAvailableLanguages();
    const matchingLang = availableLangs.find(lang => lang.startsWith(langBase));
    if (matchingLang) {
      this.setLanguage(matchingLang);
      // console.log(`Language matched: ${browserLang} -> ${matchingLang}`);
      return;
    }

    // Priority 3: Default to fallback language
    // console.log('Using fallback language:', this.fallbackLanguage);
    this.setLanguage(this.fallbackLanguage);
  }
}

// Create and export a singleton instance
const i18n = new I18n();

export default i18n;

